var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Mesh as ThreeMesh, MeshBasicMaterial, ShaderMaterial, DoubleSide, AlwaysStencilFunc, NotEqualStencilFunc, ReplaceStencilOp, Color, BufferAttribute, BufferGeometry, Matrix4, Quaternion, Scene, Vector3 } from "three";
import { CollisionFacet } from "flavr-common";
import { RequestCollisionShapeGeometryMessage, RequestCollisionShapeLocalTransformMessage, ResponseCollisionShapeGeometryMessage, ResponseCollisionShapeLocalTransformMessage } from "@messages/selection";
import { isSessionMessage } from "@modules/module";
import { ObjectPool } from "./ObjectPool";
// Outline shader: expands each vertex away from the object's view-space centre.
// This is stable for hard-edged meshes (boxes, etc.) where face normals can be
// parallel to the view ray and produce degenerate expansion directions.
const OUTLINE_VERTEX_SHADER = `
uniform float outlineThickness;
void main() {
  vec3 viewPos    = (modelViewMatrix * vec4(position, 1.0)).xyz;

  // modelViewMatrix[3].xyz is the view-space position of the model origin.
  vec3 viewCenter = modelViewMatrix[3].xyz;

  // Expand this vertex away from the object centre in view space.
  vec3 expandDir  = normalize(viewPos - viewCenter);

  vec4 clipPos      = projectionMatrix * vec4(viewPos, 1.0);
  vec4 clipExpanded = projectionMatrix * vec4(viewPos + expandDir, 1.0);

  // Compute expansion direction in 2D NDC for uniform screen-space width.
  vec2 dir = normalize(clipExpanded.xy / clipExpanded.w - clipPos.xy / clipPos.w);

  gl_Position = clipPos + vec4(dir * outlineThickness * clipPos.w, 0.0, 0.0);
}
`;
const OUTLINE_FRAGMENT_SHADER = `
uniform vec3 outlineColor;
uniform float outlineAlpha;
void main() {
  gl_FragColor = vec4(outlineColor, outlineAlpha);
}
`;
export class SelectionHighlightManager {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    constructor(instanceBatchManager) {
        this.instanceBatchManager = instanceBatchManager;
        this.tmpHighlightPosition = new Vector3();
        this.tmpHighlightOrientation = new Quaternion();
        this.tmpHighlightScale = new Vector3();
        this.tmpCollisionHighlightMatrix = new Matrix4();
        this.outlineMeshPool = new ObjectPool(ThreeMesh, 3);
        this.stencilMeshPool = new ObjectPool(ThreeMesh, 3);
        this.highlightsForControllers = new Map();
        this.highlightsForModels = new Map();
        this.highlightGeneration = 0;
        this.outlineScene = new Scene();
        // Shared stencil mask material: writes 1 into the stencil for every pixel the
        // object covers (front and back faces, ignoring depth so occluded parts count too).
        this.stencilMaterial = new MeshBasicMaterial({
            colorWrite: false,
            depthWrite: false,
            depthTest: false,
            side: DoubleSide,
            stencilWrite: true,
            stencilFunc: AlwaysStencilFunc,
            stencilRef: 1,
            stencilFail: ReplaceStencilOp,
            stencilZFail: ReplaceStencilOp,
            stencilZPass: ReplaceStencilOp,
        });
        this.hiddenSelectionAfterRendering = new Set();
    }
    get selectionOutlineScene() { return this.outlineScene; }
    onRemoveModel(uid) {
        const highlights = this.highlightsForModels.get(uid);
        if (highlights === undefined)
            return;
        for (const highlight of [...highlights]) {
            this.deleteHighlight(highlight.controllerUid, uid);
        }
        this.hiddenSelectionAfterRendering.delete(uid);
    }
    handleModelTransformationUpdate(uid, model) {
        var _a;
        const highlights = this.highlightsForModels.get(uid);
        if (highlights === undefined)
            return;
        for (const highlight of highlights) {
            const mesh = highlight.mesh;
            if (highlight.collisionLocalTransform !== undefined) {
                this.tmpCollisionHighlightMatrix.multiplyMatrices(model.matrixWorld, highlight.collisionLocalTransform);
                this.tmpCollisionHighlightMatrix.decompose(this.tmpHighlightPosition, this.tmpHighlightOrientation, this.tmpHighlightScale);
                mesh.position.copy(this.tmpHighlightPosition);
                mesh.quaternion.copy(this.tmpHighlightOrientation);
                mesh.scale.copy(this.tmpHighlightScale);
                mesh.updateMatrix();
            }
            else {
                const info = (_a = model.renderable) === null || _a === void 0 ? void 0 : _a.getGeometry();
                if ((info === null || info === void 0 ? void 0 : info.geometry) !== undefined) {
                    mesh.geometry = info.geometry;
                    highlight.stencilMesh.geometry = info.geometry;
                }
                if ((info === null || info === void 0 ? void 0 : info.mesh) !== undefined) {
                    info.mesh.updateWorldMatrix(true, false);
                    info.mesh.matrixWorld.decompose(this.tmpHighlightPosition, this.tmpHighlightOrientation, this.tmpHighlightScale);
                    mesh.position.copy(this.tmpHighlightPosition);
                    mesh.quaternion.copy(this.tmpHighlightOrientation);
                    mesh.scale.copy(this.tmpHighlightScale);
                    mesh.updateMatrix();
                }
                else {
                    mesh.position.copy(model.worldPosition);
                    mesh.quaternion.copy(model.worldOrientation);
                    mesh.scale.copy(model.worldScale);
                    mesh.updateMatrix();
                }
            }
            mesh.matrixWorldNeedsUpdate = true;
            // Keep stencil mesh in sync
            highlight.stencilMesh.matrix.copy(mesh.matrix);
            highlight.stencilMesh.matrixWorldNeedsUpdate = true;
        }
    }
    handleSelectEventMessage(target, msg, meta) {
        return __awaiter(this, void 0, void 0, function* () {
            if (msg.isSelected) {
                const isRemote = isSessionMessage(meta);
                yield this.addSelectionHighlight(msg.controllerUid, target, isRemote, msg.color);
            }
            else {
                this.removeSelectionHighlights(msg.controllerUid);
            }
        });
    }
    getSelectionHighlightMesh(model) {
        return __awaiter(this, void 0, void 0, function* () {
            let geometry;
            let matrixWorld;
            if (model.renderable !== undefined) {
                const info = model.renderable.getGeometry();
                if (info !== undefined) {
                    geometry = info.geometry;
                    if (info.mesh !== undefined) {
                        matrixWorld = info.mesh.matrixWorld;
                    }
                }
            }
            if (geometry === undefined && model.collisionShape !== undefined) {
                if (CollisionFacet.isPrimitiveShape(model.collisionShape)) {
                    geometry = this.instanceBatchManager.geometries.get(model.collisionShape);
                }
                else {
                    // Ask CollisionWorld for whatever geometry is actually committed for this uid, rather than rebuilding it
                    // from model.collisionShape here. For a compound shape in particular, model.collisionShape only ever
                    // reflects the originally authored (typically empty) placeholder - CompoundColliderManager derives the
                    // real element list live and mutates the facet's shape in place rather than via setFacet (to avoid an
                    // infinite rebuild loop through CollisionModule's own facet handler), so this renderer-side mirror never
                    // sees the resolved elements.
                    geometry = yield this.requestCollisionShapeGeometry(model.modelUID, model.renderer.getConnection());
                }
                if (geometry === undefined) {
                    return undefined;
                }
                const localTransform = yield this.requestCollisionShapeLocalTransform(model.modelUID, model.renderer.getConnection());
                if (localTransform === undefined) {
                    return undefined;
                }
                matrixWorld = new Matrix4().copy(model.matrixWorld).multiply(localTransform);
                const glow = this.outlineMeshPool.getFromPool();
                glow.geometry = geometry;
                glow.matrixAutoUpdate = false;
                glow.castShadow = false;
                return { glow, matrixWorld, collisionLocalTransform: localTransform };
            }
            if (geometry === undefined) {
                return undefined;
            }
            const glow = this.outlineMeshPool.getFromPool();
            glow.geometry = geometry;
            glow.matrixAutoUpdate = false;
            glow.castShadow = false;
            return { glow, matrixWorld };
        });
    }
    /**
     * Fetches the geometry CollisionWorld already has committed for uid, instead of rebuilding it locally - see the
     * call site for why this matters for compound shapes. Returns undefined (no highlight) rather than throwing if
     * uid has no collision shape (yet), e.g. a compound shape whose elements haven't resolved at all.
     */
    requestCollisionShapeGeometry(uid, connection) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield connection.askByMessageType(new RequestCollisionShapeGeometryMessage(uid), ResponseCollisionShapeGeometryMessage);
                if (response.vertices.length === 0)
                    return undefined;
                const geometry = new BufferGeometry();
                geometry.setAttribute("position", new BufferAttribute(new Float32Array(response.vertices), 3));
                if (response.index.length > 0)
                    geometry.setIndex(response.index);
                geometry.computeVertexNormals();
                return geometry;
            }
            catch (error) {
                console.warn(`[selection] Failed to fetch collision shape geometry for uid ${uid}`, error);
                return undefined;
            }
        });
    }
    requestCollisionShapeLocalTransform(uid, connection) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield connection.askByMessageType(new RequestCollisionShapeLocalTransformMessage(uid), ResponseCollisionShapeLocalTransformMessage);
                return new Matrix4().fromArray(response.localTransform);
            }
            catch (error) {
                console.warn(`[selection] Failed to fetch collision shape local transform for uid ${uid}`, error);
                return undefined;
            }
        });
    }
    createOutlineMaterial(color, alpha) {
        return new ShaderMaterial({
            uniforms: {
                outlineThickness: { value: 0.005 },
                outlineColor: { value: new Color().fromArray(color).convertLinearToSRGB() },
                outlineAlpha: { value: alpha }
            },
            vertexShader: OUTLINE_VERTEX_SHADER,
            fragmentShader: OUTLINE_FRAGMENT_SHADER,
            side: DoubleSide,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            stencilWrite: true,
            stencilFunc: NotEqualStencilFunc,
            stencilRef: 1,
        });
    }
    addSelectionHighlight(controllerUID, target, isRemote, color) {
        return __awaiter(this, void 0, void 0, function* () {
            const generation = this.highlightGeneration;
            const glowData = yield this.getSelectionHighlightMesh(target);
            if (glowData === undefined)
                return;
            if (generation !== this.highlightGeneration) {
                this.outlineMeshPool.returnToPool(glowData.glow);
                return;
            }
            const outlineColor = color !== undefined
                ? new Color(color).toArray()
                : isRemote
                    ? [0.3, 0.3, 0.3]
                    : [0, 0, 0];
            const alpha = isRemote ? 0.3 : 0.8;
            glowData.glow.material = this.createOutlineMaterial(outlineColor, alpha);
            glowData.glow.renderOrder = 1;
            glowData.glow.visible = true;
            if (glowData.matrixWorld !== undefined) {
                glowData.matrixWorld.decompose(this.tmpHighlightPosition, this.tmpHighlightOrientation, this.tmpHighlightScale);
                glowData.glow.position.copy(this.tmpHighlightPosition);
                glowData.glow.quaternion.copy(this.tmpHighlightOrientation);
                glowData.glow.scale.copy(this.tmpHighlightScale);
                glowData.glow.updateMatrix();
                glowData.glow.matrixWorldNeedsUpdate = true;
            }
            else {
                target.getWorldTransform(glowData.glow.position, glowData.glow.quaternion, glowData.glow.scale);
                glowData.glow.updateMatrix();
            }
            // Stencil mask: same geometry and transform, renders first (renderOrder 0),
            // fills stencil=1 everywhere the object projects so the outline only draws at edges.
            const stencilMesh = this.stencilMeshPool.getFromPool();
            stencilMesh.geometry = glowData.glow.geometry;
            stencilMesh.material = this.stencilMaterial;
            stencilMesh.matrixAutoUpdate = false;
            stencilMesh.castShadow = false;
            stencilMesh.renderOrder = 0;
            stencilMesh.matrix.copy(glowData.glow.matrix);
            stencilMesh.matrixWorldNeedsUpdate = true;
            stencilMesh.visible = true;
            this.outlineScene.add(glowData.glow);
            this.outlineScene.add(stencilMesh);
            const highlight = {
                mesh: glowData.glow,
                stencilMesh,
                uid: target.modelUID,
                controllerUid: controllerUID,
                collisionLocalTransform: glowData.collisionLocalTransform
            };
            const existingSelection = this.highlightsForControllers.get(controllerUID);
            if (existingSelection !== undefined) {
                existingSelection.push(highlight);
            }
            else {
                this.highlightsForControllers.set(controllerUID, [highlight]);
            }
            const existingHighlights = this.highlightsForModels.get(target.modelUID);
            if (existingHighlights !== undefined) {
                existingHighlights.push(highlight);
            }
            else {
                this.highlightsForModels.set(target.modelUID, [highlight]);
            }
        });
    }
    clearSelectionHighlights() {
        this.highlightGeneration++;
        for (const controllerUID of this.highlightsForControllers.keys()) {
            this.removeSelectionHighlights(controllerUID);
        }
        this.hiddenSelectionAfterRendering.clear();
    }
    /**
     * removes selection highlights
     * @param controllerUID the controller for which the selection highlights should be removed
     * @param uids the ids of the modes for which selection highlights should be removed,
     * or undefined if the selection highlights should be removed for all models highlighted by the
     * controller
     */
    removeSelectionHighlights(controllerUID, uids) {
        if (uids !== undefined) {
            for (const uid of uids) {
                this.deleteHighlight(controllerUID, uid);
            }
            return;
        }
        const existingHighlights = this.highlightsForControllers.get(controllerUID);
        if (existingHighlights === undefined)
            return;
        for (const highlight of [...existingHighlights]) {
            this.deleteHighlight(controllerUID, highlight.uid);
        }
        this.highlightsForControllers.delete(controllerUID);
    }
    deleteHighlight(controllerUid, uid) {
        let highlightsForController = this.highlightsForControllers.get(controllerUid);
        let highlightsForModel = this.highlightsForModels.get(uid);
        if (highlightsForController === undefined || highlightsForModel === undefined)
            return;
        for (const highlight of highlightsForModel) {
            if (highlight.controllerUid === controllerUid) {
                this.outlineScene.remove(highlight.mesh);
                if (highlight.mesh.material instanceof ShaderMaterial) {
                    highlight.mesh.material.dispose();
                }
                this.outlineMeshPool.returnToPool(highlight.mesh);
                this.outlineScene.remove(highlight.stencilMesh);
                this.stencilMeshPool.returnToPool(highlight.stencilMesh);
            }
        }
        highlightsForController = highlightsForController.filter((it) => it.uid !== uid);
        highlightsForModel = highlightsForModel.filter((it) => it.controllerUid !== controllerUid);
        if (highlightsForController.length === 0) {
            this.highlightsForControllers.delete(controllerUid);
        }
        else {
            this.highlightsForControllers.set(controllerUid, highlightsForController);
        }
        if (highlightsForModel.length === 0) {
            this.highlightsForModels.delete(uid);
        }
        else {
            this.highlightsForModels.set(uid, highlightsForModel);
        }
    }
    onBeforeRender() {
        for (const uid of this.hiddenSelectionAfterRendering) {
            const highlights = this.highlightsForModels.get(uid);
            if (highlights === undefined)
                continue;
            for (const highlight of highlights) {
                highlight.mesh.visible = true;
                highlight.stencilMesh.visible = true;
            }
        }
    }
    onAfterRender() {
        for (const uid of this.hiddenSelectionAfterRendering) {
            const highlights = this.highlightsForModels.get(uid);
            if (highlights === undefined)
                continue;
            for (const highlight of highlights) {
                highlight.mesh.visible = false;
                highlight.stencilMesh.visible = false;
            }
        }
    }
}
